import React, { useState, useEffect, useMemo } from 'react';
import { AlertCircle, Activity, Linkedin, UserPlus, CheckCircle2, MessageSquare, Calendar, Trophy, DollarSign, MessageSquareReply, ThumbsUp } from 'lucide-react';
import { useLinkedInAgentLeads, useLinkedInMessageLogs, useMeetingsBooked } from '../../hooks/useSupabaseLeads';
import { usePipelineDeals } from '../../hooks/usePipelineDeals';
import AgentHeader from '../CallAgent/AgentHeader';
import AgentActionPanel from '../CallAgent/AgentActionPanel';
import TodayHighlights from '../CallAgent/TodayHighlights';
import ActivityFeed from '../CallAgent/ActivityFeed';
import PerformanceIntelligence from '../shared/PerformanceIntelligence';
import AgentMilestones from '../shared/AgentMilestones';
import type { MilestoneTier } from '../shared/AgentMilestones';
import NextActionsPanel from '../CallAgent/NextActionsPanel';
import TestAgentModal from '../CallAgent/TestAgentModal';
import LinkedInHowIWorkModal from './LinkedInHowIWorkModal';
import FeedbackPopup from '../FeedbackPopup';
import LoadingScreen from '../shared/LoadingScreen';
import { getAgentSettingsRow } from '../../services/settingsService';
import { useTasksQueue } from '../../hooks/useTasksQueue';

const LinkedInAgentPage = () => {
  const { data: allLinkedInLeads, isLoading, error } = useLinkedInAgentLeads();
  const taskQueue = useTasksQueue('linkedinagent');
  const { data: pipelineDeals } = usePipelineDeals();
  const { data: liMessageLogs } = useLinkedInMessageLogs();
  const { data: allMeetings } = useMeetingsBooked();
  const [showTestAgentModal, setShowTestAgentModal] = useState(false);
  const [showHowIWorkModal, setShowHowIWorkModal] = useState(false);
  const [agentAvatarUrl, setAgentAvatarUrl] = useState<string | undefined>(undefined);

  useEffect(() => {
    getAgentSettingsRow('linkedin').then(row => {
      if (row?.avatar_url) setAgentAvatarUrl(row.avatar_url);
    });
  }, []);

  const allLinkedInLogs = useMemo(() => {
    return [...allLinkedInLeads].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  }, [allLinkedInLeads]);

  const todayStart = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  }, []);

  const todayEnd = useMemo(() => {
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    return today;
  }, []);

  const todayLinkedInLogs = useMemo(() => {
    return allLinkedInLogs.filter(log => {
      const logDate = new Date(log.created_at);
      return logDate >= todayStart && logDate <= todayEnd;
    });
  }, [allLinkedInLogs, todayStart, todayEnd]);

  const todayLiLogs = useMemo(() => {
    return liMessageLogs.filter(log => {
      const d = new Date(log.created_at);
      return d >= todayStart && d <= todayEnd;
    });
  }, [liMessageLogs, todayStart, todayEnd]);

  const todayStats = useMemo(() => {
    const connectionsSent = allLinkedInLeads.filter(lead => {
      if (!lead.la_cr_sent_at) return false;
      const d = new Date(lead.la_cr_sent_at);
      return d >= todayStart && d <= todayEnd;
    }).length;

    const connectionsAccepted = allLinkedInLeads.filter(lead => {
      if (!lead.la_cr_accepted_at) return false;
      const d = new Date(lead.la_cr_accepted_at);
      return d >= todayStart && d <= todayEnd;
    }).length;

    const sentLogs = todayLiLogs.filter(l => l.from_role === 'user');
    const messagesSent = sentLogs.length;

    const repliesReceived = todayLiLogs.filter(l => l.from_role === 'prospect').length;

    const repliedLeadIds = new Set(todayLiLogs.filter(l => l.from_role === 'prospect').map(l => l.lead_id).filter(Boolean));
    const positiveConversations = allLinkedInLeads.filter(lead => {
      if (!repliedLeadIds.has(lead.lead_id)) return false;
      const temp = (lead.la_lead_temp || '').toLowerCase();
      return temp === 'warm' || temp === 'hot';
    }).length;

    const todayMeetings = allMeetings.filter(m => {
      const d = new Date(m.created_at);
      const channel = (m.booked_channel || '').toLowerCase();
      return d >= todayStart && d <= todayEnd && channel.includes('linkedin');
    }).length;

    return { connectionsSent, connectionsAccepted, messagesSent, repliesReceived, positiveConversations, meetingsBooked: todayMeetings };
  }, [todayLiLogs, allLinkedInLeads, allMeetings, todayStart, todayEnd]);

  const agentStatus = useMemo(() => {
    if (allLinkedInLogs.length === 0) return 'idle';

    const lastAction = allLinkedInLogs[0];
    const lastActionTime = new Date(lastAction.created_at);
    const now = new Date();
    const hoursSinceLastAction = (now.getTime() - lastActionTime.getTime()) / (1000 * 60 * 60);

    if (hoursSinceLastAction < 1) return 'active';
    if (allLinkedInLeads.some(lead => lead.la_next_fu_datetime && new Date(lead.la_next_fu_datetime) > now)) return 'idle';
    return 'processing';
  }, [allLinkedInLogs, allLinkedInLeads]);

  const meetingsBooked = useMemo(() => {
    return allMeetings.filter(m => {
      const channel = (m.booked_channel || '').toLowerCase();
      return channel.includes('linkedin');
    }).length;
  }, [allMeetings]);

  const newConnections = useMemo(() => {
    return allLinkedInLeads.filter(lead =>
      (lead.la_connection_status || '').toLowerCase() === 'connected'
    ).length;
  }, [allLinkedInLeads]);

  const pipelineValue = useMemo(() => {
    return pipelineDeals
      .filter(d => (d.channel_booked || '').toLowerCase().includes('linkedin'))
      .reduce((sum, d) => sum + (d.deal_value || 0), 0);
  }, [pipelineDeals]);

  const milestoneTiers: MilestoneTier[] = useMemo(() => [
    {
      icon: Trophy,
      title: '500 New Connections',
      target: 500,
      reward: 'Get 200 Free LinkedIn Credits',
      currentValue: newConnections,
      formatValue: (c, t) => `${c.toLocaleString()} / ${t.toLocaleString()} connections`,
    },
    {
      icon: Calendar,
      title: '10 Meetings Booked',
      target: 10,
      reward: '500 Free LinkedIn Credits',
      currentValue: meetingsBooked,
      formatValue: (c, t) => `${c} / ${t} meetings booked`,
    },
    {
      icon: DollarSign,
      title: '$150,000 Pipeline Generated',
      target: 150000,
      reward: '1,000 Free LinkedIn Credits',
      currentValue: pipelineValue,
      formatValue: (c, t) => `$${c.toLocaleString()} / $${t.toLocaleString()}`,
    },
  ], [newConnections, meetingsBooked, pipelineValue]);


  if (isLoading && allLinkedInLeads.length === 0) {
    return <LoadingScreen />;
  }

  return (
    <div>
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 p-3 rounded-lg flex items-center space-x-2 mb-6">
          <AlertCircle size={20} />
          <span>{error}</span>
        </div>
      )}

      <AgentHeader
        status={agentStatus}
        title="LinkedIn Agent"
        subtitle="I connect with prospects on LinkedIn, build relationships, and book meetings."
        icon="linkedin"
        avatarSrc={agentAvatarUrl}
        avatarAlt="LinkedIn Agent"
        activeLabel="Active — Messaging Prospects"
        processingMessages={[
          'Preparing LI insights…',
          'Checking connection acceptance…',
          'Analyzing profile engagement…',
          'Crafting personalized messages…',
          'Visiting target profiles…',
          'Scheduling follow-ups…'
        ]}
      />

      <AgentActionPanel
        onTestCall={() => setShowTestAgentModal(true)}
        onHowIWork={() => setShowHowIWorkModal(true)}
        campaignPath="/campaigns/linkedin"
        settingsPath="linkedin-agent"
        howIWorkDescription="My process for LinkedIn outreach and conversations."
        viewCampaignDescription="See the LinkedIn outreach sequence and campaign touchpoints."
        testMeDescription="Test my outreach - paste your LinkedIn profile."
        testMeIcon={Linkedin}
        customizeMeDescription="Configure persona, messaging style, and outreach sequence."
      />

      <div className="mt-[60px]">
        <TodayHighlights
          metrics={[
            { value: todayStats.connectionsSent, label: 'CR Sent', icon: UserPlus },
            { value: todayStats.connectionsAccepted, label: 'CR Accepted', icon: CheckCircle2 },
            { value: todayStats.messagesSent, label: 'Messages Sent', icon: Linkedin },
            { value: todayStats.repliesReceived, label: 'Replies Received', icon: MessageSquareReply },
            { value: todayStats.positiveConversations, label: 'Positive Conversations', icon: ThumbsUp },
            { value: todayStats.meetingsBooked, label: 'Meetings Booked', icon: Calendar },
          ]}
        />
      </div>

      <div className="space-y-3 mt-[60px]">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-gradient-to-br from-[#1875FF] to-[#05E0D1] rounded-xl flex items-center justify-center shadow-lg">
            <Activity className="text-white" size={22} />
          </div>
          <h2 className="text-2xl font-extrabold text-gray-900 tracking-tight font-display">Live Operations</h2>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" style={{ minHeight: '600px' }}>
          <ActivityFeed
            processingTasks={taskQueue.processingTasks}
            recentFinishedTasks={taskQueue.recentFinishedTasks}
            hasProcessing={taskQueue.hasProcessing}
            hasAnyTasks={taskQueue.hasAnyTasks}
            isLoading={taskQueue.isLoading}
            emptyState={{ icon: Linkedin, title: 'Waiting for LinkedIn tasks', subtitle: 'Assign leads to start LinkedIn outreach' }}
          />
          <NextActionsPanel
            pendingTasks={taskQueue.pendingTasks}
            hasAnyTasks={taskQueue.hasAnyTasks}
            isLoading={taskQueue.isLoading}
            emptyState={{ icon: Linkedin, title: 'No scheduled actions', subtitle: 'LinkedIn actions will appear as leads are assigned' }}
          />
        </div>
      </div>

      <div className="mt-[60px]">
        <PerformanceIntelligence agentType="linkedin" />
      </div>

      <div className="mt-[60px]">
        <AgentMilestones tiers={milestoneTiers} />
      </div>

      <TestAgentModal
        isOpen={showTestAgentModal}
        onClose={() => setShowTestAgentModal(false)}
        agentType="linkedin"
      />

      <LinkedInHowIWorkModal
        isOpen={showHowIWorkModal}
        onClose={() => setShowHowIWorkModal(false)}
      />

      <FeedbackPopup isVisible={true} />
    </div>
  );
};

export default LinkedInAgentPage;
